import { CircleX, Info } from 'lucide-react'
import { useRef } from 'react'

import { Button } from '@/components/ui/button'
import { IndeterminateLoadingBar } from '@/components/ui/IndeterminateLoadingBar'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CarrierRatingValue } from '@/models/insuranceCarrier'
import { COVERAGE_TYPE_LABELS, CoverageType } from '@/models/insuranceCoverage'
import { FilterOption, FilterPopover } from './FilterPopover'
import { type ClaimsCapabilities, CoiSpeed } from './quoteCardTypes'
import { QuotesEvent, QuotesFilter, useTrackQuotesEvent } from './quotesAnalytics'
import { useScrollHint } from './useScrollHint'

export type QuoteSortBy = 'best' | 'cheapest'

// Letter buckets: quoteFilters matches carrierRating[0], and E/F are not real ratings.
const RATINGS = [...new Set(Object.values(CarrierRatingValue).map((rating) => rating[0]))]

const COI_SPEED_OPTIONS: { value: CoiSpeed; label: string }[] = [
  { value: CoiSpeed.UNDER_24_HOURS, label: 'COI under 24 hours' },
  { value: CoiSpeed.ONE_DAY_OR_MORE, label: 'COI 1 day or more' },
]

/**
 * Only the coverages Cai actually requests from carriers. Derived from CoverageType
 * rather than listed by hand: the prototype also offered Workers Compensation and
 * Cyber as bare strings, which aren't in the enum and which no carrier here quotes,
 * so filtering on them could only ever return nothing.
 */
export const COVERAGE_FILTER_OPTIONS: { value: string; label: string }[] = Object.values(CoverageType).map(
  (coverageType) => ({ value: coverageType, label: COVERAGE_TYPE_LABELS[coverageType] })
)

export type ClaimsCapabilityFilterKey = 'multiple-ways-to-file' | '24-7-support' | 'has-payout-info'

const CLAIMS_CAPABILITY_OPTIONS: { value: ClaimsCapabilityFilterKey; label: string }[] = [
  { value: 'multiple-ways-to-file', label: 'Multiple ways to file' },
  { value: '24-7-support', label: '24/7 support' },
  { value: 'has-payout-info', label: 'Has payout information' },
]

// Each criterion reads a curated boolean off the carrier record. These used to be
// heuristics over the prose ("does ways-to-file contain the word 'or'?"), which
// silently misjudged any carrier that phrased its offering differently.
export function matchesClaimsCapability(
  claims: ClaimsCapabilities | undefined,
  key: ClaimsCapabilityFilterKey
): boolean {
  if (!claims) return false
  switch (key) {
    case 'multiple-ways-to-file':
      return claims.hasMultipleWaysToFile
    case '24-7-support':
      return claims.has247Support
    case 'has-payout-info':
      return claims.hasPayoutInformation
  }
}

interface FiltersSectionProps {
  sortBy: QuoteSortBy
  onSortByChange: (value: QuoteSortBy) => void
  selectedRatings: Set<string>
  onToggleRating: (rating: string) => void
  onClearRatings: () => void
  selectedCoiSpeeds: Set<CoiSpeed>
  onToggleCoiSpeed: (speed: CoiSpeed) => void
  onClearCoiSpeeds: () => void
  selectedCoverages: Set<string>
  onToggleCoverage: (coverage: string) => void
  onClearCoverages: () => void
  financingAvailable: boolean
  onToggleFinancingAvailable: () => void
  onClearFinancingAvailable: () => void
  selectedClaimsCapabilities: Set<ClaimsCapabilityFilterKey>
  onToggleClaimsCapability: (key: ClaimsCapabilityFilterKey) => void
  onClearClaimsCapabilities: () => void
  onClearAllFilters: () => void
  /** Shows a sliding progress bar above the controls while the quote list below is re-sorting/filtering. */
  isUpdatingQuotes: boolean
  /**
   * Greys out Best/Cheapest. Set while fewer than two cards are priced: the toggle is
   * what the page is for, so it stays on show rather than being dropped, but there's
   * nothing to rank.
   */
  sortDisabled?: boolean
}

/**
 * Filters bar for the Quotes page. Every trigger keeps a constant label and width — see
 * {@link FilterPopover} — so ticking an option only re-orders the cards below, never the
 * filters themselves.
 */
export function FiltersSection({
  sortBy,
  onSortByChange,
  selectedRatings,
  onToggleRating,
  onClearRatings,
  selectedCoiSpeeds,
  onToggleCoiSpeed,
  onClearCoiSpeeds,
  selectedCoverages,
  onToggleCoverage,
  onClearCoverages,
  financingAvailable,
  onToggleFinancingAvailable,
  onClearFinancingAvailable,
  selectedClaimsCapabilities,
  onToggleClaimsCapability,
  onClearClaimsCapabilities,
  onClearAllFilters,
  isUpdatingQuotes,
  sortDisabled,
}: FiltersSectionProps) {
  const track = useTrackQuotesEvent()
  const filterRowRef = useRef<HTMLDivElement>(null)
  useScrollHint(filterRowRef)
  const ratingCount = RATINGS.filter((rating) => selectedRatings.has(rating)).length
  const coiSpeedCount = COI_SPEED_OPTIONS.filter((option) => selectedCoiSpeeds.has(option.value)).length
  const coverageCount = COVERAGE_FILTER_OPTIONS.filter((option) => selectedCoverages.has(option.value)).length
  const claimsCapabilityCount = CLAIMS_CAPABILITY_OPTIONS.filter((option) =>
    selectedClaimsCapabilities.has(option.value)
  ).length

  const hasActiveFilters =
    ratingCount > 0 ||
    coiSpeedCount > 0 ||
    coverageCount > 0 ||
    financingAvailable ||
    claimsCapabilityCount > 0

  return (
    <section className="mb-8">
      <div className="mb-3 h-1">
        <IndeterminateLoadingBar
          className={cn(
            'rounded-full transition-opacity duration-300',
            isUpdatingQuotes ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
      {/* Mobile bleeds past the page gutter so swiped filters run to the screen edges. */}
      <div className="relative max-md:-mx-4">
        {/* Reserved unconditionally for Clear all: added with the first filter, it dragged the row left. */}
        <div
          ref={filterRowRef}
          className="flex flex-nowrap items-center gap-3 overflow-x-auto pr-32 [scrollbar-width:none] max-md:snap-x max-md:snap-mandatory max-md:scroll-pl-4 max-md:pl-4 max-md:*:snap-start md:pr-26 [&::-webkit-scrollbar]:hidden"
        >
          {/* Fills the row's spare width on desktop, so it grows or shrinks as filter counts change. */}
          <Tabs
            value={sortBy}
            onValueChange={(value) => {
              track(QuotesEvent.SORT_CHANGED, { sort_by: value, previous_sort_by: sortBy })
              onSortByChange(value as QuoteSortBy)
            }}
            className="w-52 shrink-0 md:w-auto md:min-w-52 md:flex-1"
          >
            <TabsList className="border-ink-100 w-full gap-0 rounded-xl border bg-transparent p-0.5 data-[orientation=horizontal]:h-8">
              <TabsTrigger
                value="best"
                disabled={sortDisabled}
                className="data-[state=active]:bg-brand-100 gap-1 rounded-lg border-none data-[state=active]:shadow-none"
              >
                Best
                <Tooltip>
                  {/* span, not a button — this label already sits inside the tab's own <button> */}
                  <TooltipTrigger asChild>
                    <span tabIndex={-1} aria-label="How we rank Best" className="max-md:hidden">
                      <Info className="text-ink-400 size-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Carriers that priced every coverage
                    <br />
                    you asked for, cheapest first.
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
              <TabsTrigger
                value="cheapest"
                disabled={sortDisabled}
                className="data-[state=active]:bg-brand-100 rounded-lg border-none data-[state=active]:shadow-none"
              >
                Cheapest
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <FilterPopover
            filter={QuotesFilter.COVERAGES}
            label="Coverages"
            count={coverageCount}
            panelClassName="w-52"
            onClear={onClearCoverages}
          >
            {COVERAGE_FILTER_OPTIONS.map((option) => (
              <FilterOption
                key={option.value}
                filter={QuotesFilter.COVERAGES}
                value={option.value}
                label={option.label}
                checked={selectedCoverages.has(option.value)}
                onToggle={() => onToggleCoverage(option.value)}
              />
            ))}
          </FilterPopover>

          <FilterPopover
            filter={QuotesFilter.RATING}
            label="Rating"
            count={ratingCount}
            panelClassName="w-40"
            onClear={onClearRatings}
          >
            {RATINGS.map((rating) => (
              <FilterOption
                key={rating}
                filter={QuotesFilter.RATING}
                value={rating}
                label={rating}
                checked={selectedRatings.has(rating)}
                onToggle={() => onToggleRating(rating)}
              />
            ))}
          </FilterPopover>

          <FilterPopover
            filter={QuotesFilter.FINANCING}
            label="Financing available"
            count={financingAvailable ? 1 : 0}
            panelClassName="w-64"
            onClear={onClearFinancingAvailable}
          >
            <FilterOption
              filter={QuotesFilter.FINANCING}
              value="typically_available"
              label="Typically available"
              checked={financingAvailable}
              onToggle={onToggleFinancingAvailable}
            />
          </FilterPopover>

          <FilterPopover
            filter={QuotesFilter.CLAIMS_CAPABILITIES}
            label="Claims capabilities"
            count={claimsCapabilityCount}
            panelClassName="w-56"
            onClear={onClearClaimsCapabilities}
          >
            {CLAIMS_CAPABILITY_OPTIONS.map((option) => (
              <FilterOption
                key={option.value}
                filter={QuotesFilter.CLAIMS_CAPABILITIES}
                value={option.value}
                label={option.label}
                checked={selectedClaimsCapabilities.has(option.value)}
                onToggle={() => onToggleClaimsCapability(option.value)}
              />
            ))}
          </FilterPopover>

          <FilterPopover
            filter={QuotesFilter.COI_SPEED}
            label="COI speed"
            count={coiSpeedCount}
            panelClassName="w-56"
            onClear={onClearCoiSpeeds}
          >
            {COI_SPEED_OPTIONS.map((option) => (
              <FilterOption
                key={option.value}
                filter={QuotesFilter.COI_SPEED}
                value={option.value}
                label={option.label}
                checked={selectedCoiSpeeds.has(option.value)}
                onToggle={() => onToggleCoiSpeed(option.value)}
              />
            ))}
          </FilterPopover>
        </div>

        {hasActiveFilters && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
            <div aria-hidden="true" className="to-ink-50 h-full w-8 bg-gradient-to-r from-transparent" />
            <div className="bg-ink-50 pointer-events-auto flex h-full items-center pl-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-ink-400 hover:text-ink-600"
                onClick={() => {
                  track(QuotesEvent.FILTERS_CLEARED_ALL, {
                    coverage_count: coverageCount,
                    rating_count: ratingCount,
                    claims_capability_count: claimsCapabilityCount,
                    coi_speed_count: coiSpeedCount,
                    financing_available: financingAvailable,
                  })
                  onClearAllFilters()
                }}
              >
                <CircleX className="size-4" />
                <span className="max-md:hidden">Clear all</span>
                <span className="md:hidden">Clear</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
